import { defineStore } from "pinia"
import { get } from "lodash"
import { HttpService } from "@/helpers/http-service"
import { CONSTANT } from "@/helpers/constant"
import { getOrderCatalogueSearchQuery, getOrderCatalogueNameQuery } from "@/helpers/common"
import { ApiResponse } from "@/types/apiResponse"
import type { JcSetContainerProduct } from "@/types/jcFilterSystem"

// Loader-less instance — the set-price autosuggest must not trigger the global spinner.
const httpService = new HttpService(false)

interface State {
  loading: boolean
}

/**
 * Lightweight product-variant search for the jersey-configurator set-price container picker.
 *
 * Kept isolated from the order-catalogue store so the autosuggest never clobbers the
 * order-catalogue listing state. The search source is the only general product endpoint
 * available in the teamportal; if the backend later ships a dedicated container-SKU
 * endpoint, this is the single seam to repoint.
 */
export const useJcContainerProductStore = defineStore({
  id: "jc.container.product",
  state: (): State => ({
    loading: false,
  }),
  actions: {
    async search(term: string, limit = 10): Promise<JcSetContainerProduct[]> {
      const value = (term ?? "").trim()
      if (value.length < 2) return []

      this.loading = true
      try {
        const body = {
          page: 1,
          limit,
          // Match either name or SKU / manufacturer-number (contains + equals).
          query: [...getOrderCatalogueNameQuery(value), ...getOrderCatalogueSearchQuery(value)],
        }
        const result: ApiResponse = await httpService.post(`${CONSTANT.API.ORDER_CATALOG.FETCH_PRODUCTS}`, body)
        if (result && result.status === CONSTANT.HTTP_STATUS.OK) {
          const elements = get(result, "data.elements", []) as any[]
          return elements.map((p) => ({
            id: p.id,
            name: p.translated?.name ?? p.name,
            productNumber: p.productNumber,
            translated: p.translated,
          }))
        }
        return []
      } catch (error) {
        const errors = get(error, "response.data.errors", []) as { detail?: string }[]
        if (errors && errors.length > 0) {
          console.error(errors[0]?.detail)
        }
        console.error("Failed to search container products", error)
        return []
      } finally {
        this.loading = false
      }
    },
  },
})
